import {
  Badge, Button, Card, ErrorState, Field, Input, Row, Select, Stack, useApi, useI18n,
  type CatalogItemResponse, type CatalogType,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

const TYPES: CatalogType[] = [
  'MealType', 'Food', 'IntakeLevel', 'Mood', 'Activity', 'DiaperStatus', 'Occasion', 'MedicationForm',
];

/** A dose is measured in a unit only for a medication's form ("مل"). */
const hasUnit = (type: CatalogType) => type === 'MedicationForm';

/**
 * The options every quick action, meal and medication form chooses from (FR-CFG-01, GAPS 3.8): added, renamed,
 * reordered, retired and brought back, behind `settings.manage`.
 *
 * Nothing is deleted. A retired option leaves the pickers and stays on the entries that used it, and a renamed one
 * changes what new entries say, never what earlier ones said: each entry keeps the name it was recorded with.
 */
export function CatalogueCard() {
  const api = useApi();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [type, setType] = useState<CatalogType>('MealType');
  const [editing, setEditing] = useState<number | null>(null);

  const items = useQuery({
    queryKey: ['catalog', 'manage', type],
    queryFn: () => api.catalog.list({ type, includeInactive: true }),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['catalog'] });

  const retire = useMutation({ mutationFn: (id: number) => api.catalog.retire(id), onSuccess: refresh });
  const restore = useMutation({ mutationFn: (id: number) => api.catalog.update(id, { isActive: true }), onSuccess: refresh });

  const sorted = [...(items.data ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
  const error = items.error ?? retire.error ?? restore.error;

  return (
    <Card title={t('catalog.title')} hint={t('catalog.hint')}>
      <Stack>
        <Select aria-label={t('catalog.type')} value={type} onChange={(event) => { setType(event.target.value as CatalogType); setEditing(null); }}>
          {TYPES.map((value) => <option key={value} value={value}>{t(`catalog.type.${value}`)}</option>)}
        </Select>

        {error && <ErrorState error={error} />}
        {items.data?.length === 0 && <p className="dg-card__hint">{t('catalog.empty')}</p>}

        {sorted.map((item) => (editing === item.id ? (
          <ItemForm key={item.id} type={type} item={item} onDone={() => setEditing(null)} />
        ) : (
          <Row key={item.id} between>
            <span className="dg-row">
              {item.icon && <span aria-hidden="true">{item.icon}</span>}
              <span className="dg-stack dg-stack--tight">
                <strong>{item.nameAr}</strong>
                {(item.nameEn || item.unitAr) && (
                  <span className="dg-card__hint">{[item.nameEn, item.unitAr].filter(Boolean).join(' · ')}</span>
                )}
              </span>
              {!item.isActive && <Badge tone="neutral">{t('catalog.retired')}</Badge>}
            </span>
            <Row>
              <Button variant="ghost" aria-label={t('catalog.editOf', { name: item.nameAr })} onClick={() => setEditing(item.id)}>
                {t('action.edit')}
              </Button>
              {item.isActive ? (
                <Button variant="ghost" aria-label={t('catalog.retireOf', { name: item.nameAr })}
                  busy={retire.isPending && retire.variables === item.id} onClick={() => retire.mutate(item.id)}>
                  {t('catalog.retire')}
                </Button>
              ) : (
                <Button variant="ghost" aria-label={t('catalog.restoreOf', { name: item.nameAr })}
                  busy={restore.isPending && restore.variables === item.id} onClick={() => restore.mutate(item.id)}>
                  {t('catalog.restore')}
                </Button>
              )}
            </Row>
          </Row>
        )))}

        {/* Keyed on the next order too, so a form drawn before the list arrived starts again from the right place. */}
        {editing === null && (() => {
          const nextOrder = (sorted.at(-1)?.sortOrder ?? 0) + 1;
          return <ItemForm key={`new-${type}-${nextOrder}`} type={type} item={null} nextOrder={nextOrder} onDone={() => undefined} />;
        })()}
      </Stack>
    </Card>
  );
}

/** Adds an option of `type`, or corrects `item`. */
function ItemForm({ type, item, nextOrder = 0, onDone }: {
  type: CatalogType; item: CatalogItemResponse | null; nextOrder?: number; onDone: () => void;
}) {
  const api = useApi();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [nameAr, setNameAr] = useState(item?.nameAr ?? '');
  const [nameEn, setNameEn] = useState(item?.nameEn ?? '');
  const [icon, setIcon] = useState(item?.icon ?? '');
  const [unitAr, setUnitAr] = useState(item?.unitAr ?? '');
  const [unitEn, setUnitEn] = useState(item?.unitEn ?? '');
  const [sortOrder, setSortOrder] = useState(item?.sortOrder ?? nextOrder);

  const save = useMutation({
    mutationFn: () => {
      const units = hasUnit(type) ? { unitAr: unitAr.trim(), unitEn: unitEn.trim() } : {};
      return item
        ? api.catalog.update(item.id, { nameAr: nameAr.trim(), nameEn: nameEn.trim(), icon: icon.trim(), sortOrder, ...units })
        : api.catalog.create({
            catalogType: type, nameAr: nameAr.trim(), nameEn: nameEn.trim() || null, icon: icon.trim() || null, sortOrder,
            ...(hasUnit(type) ? { unitAr: unitAr.trim() || null, unitEn: unitEn.trim() || null } : {}),
          });
    },
    onSuccess: async () => {
      setNameAr('');
      setNameEn('');
      setIcon('');
      setUnitAr('');
      setUnitEn('');
      await queryClient.invalidateQueries({ queryKey: ['catalog'] });
      onDone();
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (nameAr.trim()) save.mutate();
  };

  return (
    <form onSubmit={submit} className="dg-stack">
      <Row className="dg-row--wrap">
        <Field label={t('catalog.nameAr')} required>
          {(props) => <Input {...props} value={nameAr} onChange={(event) => setNameAr(event.target.value)} />}
        </Field>
        <Field label={t('catalog.nameEn')}>
          {(props) => <Input {...props} dir="ltr" value={nameEn} onChange={(event) => setNameEn(event.target.value)} />}
        </Field>
        <Field label={t('catalog.icon')}>
          {(props) => <Input {...props} value={icon} onChange={(event) => setIcon(event.target.value)} />}
        </Field>
        <Field label={t('catalog.order')}>
          {(props) => (
            <Input {...props} type="number" min={0} value={sortOrder} onChange={(event) => setSortOrder(Number(event.target.value))} />
          )}
        </Field>
        {hasUnit(type) && (
          <>
            <Field label={t('catalog.unitAr')}>
              {(props) => <Input {...props} value={unitAr} onChange={(event) => setUnitAr(event.target.value)} />}
            </Field>
            <Field label={t('catalog.unitEn')}>
              {(props) => <Input {...props} dir="ltr" value={unitEn} onChange={(event) => setUnitEn(event.target.value)} />}
            </Field>
          </>
        )}
      </Row>
      {save.isError && <ErrorState error={save.error} />}
      <Row>
        <Button type="submit" variant={item ? 'primary' : 'secondary'} disabled={!nameAr.trim()} busy={save.isPending}>
          {item ? t('action.save') : t('catalog.add')}
        </Button>
        {item && <Button variant="ghost" onClick={onDone}>{t('action.cancel')}</Button>}
      </Row>
    </form>
  );
}
